import type { PropDef, PropKind, TrackDef, Vec2 } from '../core/types';
import { hash01 } from '../core/math';
import { roundedLoop } from './track-geometry';

/**
 * „Jeges Alaszka" – téli alaszkai vidék.
 * A sivatagi pályától szándékosan eltérő vonalvezetés: nagy, ívelt jobbos kör,
 * majd egy mély öböl a bal oldalon (a befagyott tó partja) – a sziluett szív/vese
 * alakú, nem lekerekített téglalap.
 * A pályán versenyenként 3 hóátfúvás és 3 jeges folt jelenik meg véletlenszerűen
 * (a World generálja a verseny seedjéből): a hófúvás lassít, a jégen megcsúszik a kocsi.
 */
const CENTERLINE: Vec2[] = roundedLoop([
  { x: 500, y: 1770, r: 310 },
  { x: 1610, y: 1965, r: 335 },
  { x: 2555, y: 1655, r: 355 },
  { x: 2830, y: 990, r: 377 },
  { x: 2390, y: 410, r: 335 },
  { x: 1445, y: 300, r: 310 },
  { x: 725, y: 600, r: 265 },
  { x: 1110, y: 990, r: 265 },
  { x: 620, y: 1150, r: 290 },
]);

const props: PropDef[] = [];
const add = (kind: PropKind, s: number, lateral: number, scale = 1, variant = 0): void => {
  props.push({ kind, s, lateral, scale, variant });
};

// --- fenyves a külső oldalon (a hófúvásos tundra) ---
for (let i = 0; i < 46; i++) {
  const s = (i + 0.3) / 46;
  const h = hash01(i * 92821 + 3);
  const h2 = hash01(i * 31337 + 17);
  if (h > 0.18) add('pine', s, 230 + h2 * 120, 0.85 + h * 0.6, Math.floor(h2 * 3));
  if (h < 0.1) add('rock', s + 0.004, 300 + h2 * 80, 0.8 + h * 0.6, Math.floor(h2 * 4));
}

// --- hóemberek a pálya szélén ---
for (const [s, lateral, scale] of [
  [0.06, 196, 1.0],
  [0.23, -190, 0.9],
  [0.44, 200, 1.1],
  [0.61, -196, 0.95],
  [0.83, 192, 1.0],
] as const) {
  add('snowman', s, lateral, scale, 0);
}

// --- belső hómező: ritkás fenyő, néha kő ---
for (let i = 0; i < 30; i++) {
  const s = (i + 0.6) / 30;
  const h = hash01(i * 7829 + 41);
  const h2 = hash01(i * 104729 + 7);
  if (h > 0.5) add('pine', s, -(178 + h * 46), 0.8 + h2 * 0.45, Math.floor(h2 * 3));
  if (h < 0.1) add('snowman', s + 0.003, -(150 + h2 * 30), 0.85, 0);
}

// --- lámpák, táblák, utcabútorok ---
for (let i = 0; i < 20; i++) add('lamp', i / 20, i % 2 === 0 ? 186 : -186, 1, i % 3);
for (let i = 0; i < 14; i++) {
  const s = (i + 0.4) / 14;
  add('sign', s, i % 2 === 0 ? 178 : -178, 1, i % 4);
}
for (let i = 0; i < 8; i++) {
  const s = (i + 0.2) / 8;
  const h = hash01(i * 5501 + 29);
  if (h < 0.4) add('cone', s, -70 + (i % 3) * 14, 1, i % 2);
  else add('crosswalk', s + 0.01, 0, 1, 0);
}

// --- jeges tócsák / hókupacok az úton ---
add('puddle', 0.34, 38, 1.5, 0);
add('puddle', 0.72, -44, 1.3, 0);

export const SNOW_TRACK: TrackDef = {
  id: 'jeges-alaszka',
  name: 'Jeges Alaszka',
  subtitle: 'Téli alaszkai vidék · 3 kör',
  description:
    'Havas alaszkai tundra: hosszú, csúszós egyenesek, nagy ívű kanyarok és egy szűk átkötő a fenyvesben. A hóátfúvások lelassítanak, a jeges foltokon megcsúszik a kocsi – a türelem itt többet ér, mint a gáz.',
  centerline: CENTERLINE,
  roadHalfWidth: 100,
  kerbWidth: 12,
  sidewalkWidth: 32,
  startS: 0.03,
  laps: 3,
  theme: 'snow',
  props,
  boostPads: [
    { s: 0.18, lateral: 0, length: 250, width: 150 },
    { s: 0.5, lateral: 0, length: 250, width: 150 },
    { s: 0.79, lateral: 0, length: 250, width: 150 },
  ],
  itemRows: [
    { s: 0.1, lateral: 0, count: 4, spacing: 54 },
    { s: 0.33, lateral: 0, count: 4, spacing: 54 },
    { s: 0.6, lateral: 0, count: 4, spacing: 54 },
    { s: 0.86, lateral: 0, count: 4, spacing: 54 },
  ],
  ambient: {
    sky: '#cfe4f2',
    asphalt: '#4a5158',
    asphaltDark: '#3f454b',
    lane: '#f4fbff',
    kerb: '#5b8fc0',
    sidewalk: '#e4eef7',
    wall: '#c7dced',
    accent: '#7fd4ff',
    shadow: 'rgba(40,70,100,0.28)',
  },
  cameraZoom: 0.95,
};
